import React, { useEffect, useState } from 'react';
import { BuildingOffice2Icon, CheckCircleIcon } from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';
import { errText } from '../../services/scheduleService';

const EMPTY = {
  practice_name: '', trading_name: '', bhf_practice_number: '', discipline_code: '', vat_number: '',
  phone: '', email: '', website: '', street_address: '', suburb: '', city: '', postal_code: '',
  doctors_may_submit: false,
  bank_name: '', bank_account_name: '', bank_account_number: '', bank_branch_code: '', statement_message: '',
};

/**
 * Practice details — the numbers a claim to a medical scheme identifies the
 * practice by (BHF / PCNS practice number, discipline), plus the hospitals'
 * practice numbers (place of service). Doctors' HPCSA numbers live in User Management.
 */
const PracticeDetails = () => {
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState([]);
  const [hospitalEdits, setHospitalEdits] = useState({});
  const [hospitalMsg, setHospitalMsg] = useState({});

  useEffect(() => {
    Promise.all([settingsService.practice(), settingsService.hospitals()])
      .then(([p, h]) => {
        if (p) {
          setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] || ''])));
          setMeta(p);
        }
        setHospitals(h);
      })
      .catch((e) => setErrors([errText(e)]));
  }, []);

  const set = (k, v) => { setSaved(false); setForm((f) => ({ ...f, [k]: v })); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors([]);
    try {
      const p = await settingsService.savePractice(form);
      setMeta(p);
      setSaved(true);
    } catch (ex) {
      setErrors(ex.response?.data?.errors || [errText(ex)]);
    } finally {
      setSaving(false);
    }
  };

  const saveHospital = async (h) => {
    const value = hospitalEdits[h.hid] ?? h.hpractice_number ?? '';
    setHospitalMsg((m) => ({ ...m, [h.hid]: 'Saving…' }));
    try {
      const updated = await settingsService.setHospitalNumber(h.hid, value);
      setHospitals((list) => list.map((x) => (x.hid === h.hid ? updated : x)));
      setHospitalEdits((ed) => { const n = { ...ed }; delete n[h.hid]; return n; });
      setHospitalMsg((m) => ({ ...m, [h.hid]: 'Saved' }));
    } catch (ex) {
      setHospitalMsg((m) => ({ ...m, [h.hid]: errText(ex) }));
    }
  };

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <BuildingOffice2Icon className="mt-0.5 h-5 w-5 text-brand-teal" />
        <div className="flex-1">
          <h2 className="font-bold text-slate-800">Practice details</h2>
          <p className="text-sm text-slate-500">
            How claims and statements identify the practice. Doctors’ HPCSA numbers, and whether they bill under their own practice number, are set in User Management.
          </p>
        </div>
      </div>

      <form onSubmit={save} className="grid grid-cols-6 gap-3 px-5 py-4">
        <Field label="Registered practice name *" className="col-span-6 md:col-span-3">
          <input className={inputCls} value={form.practice_name} onChange={(e) => set('practice_name', e.target.value)} />
        </Field>
        <Field label="Trading name" className="col-span-6 md:col-span-3">
          <input className={inputCls} value={form.trading_name} onChange={(e) => set('trading_name', e.target.value)} />
        </Field>
        <Field label="BHF practice number (PCNS)" hint="7 digits" className="col-span-3 md:col-span-2">
          <input className={`${inputCls} font-mono`} value={form.bhf_practice_number} onChange={(e) => set('bhf_practice_number', e.target.value)} maxLength={9} />
        </Field>
        <Field label="Discipline code" hint="3 digits, from the PCNS certificate" className="col-span-3 md:col-span-2">
          <input className={`${inputCls} font-mono`} value={form.discipline_code} onChange={(e) => set('discipline_code', e.target.value)} maxLength={3} />
        </Field>
        <Field label="VAT number" hint="10 digits, starts with 4 (if registered)" className="col-span-6 md:col-span-2">
          <input className={`${inputCls} font-mono`} value={form.vat_number} onChange={(e) => set('vat_number', e.target.value)} maxLength={12} />
        </Field>
        <Field label="Phone" className="col-span-6 md:col-span-2"><input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
        <Field label="Accounts email" className="col-span-6 md:col-span-2"><input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label="Website" className="col-span-6 md:col-span-2"><input className={inputCls} value={form.website} onChange={(e) => set('website', e.target.value)} /></Field>
        <Field label="Street address" className="col-span-6 md:col-span-3"><input className={inputCls} value={form.street_address} onChange={(e) => set('street_address', e.target.value)} /></Field>
        <Field label="Suburb" className="col-span-2 md:col-span-1"><input className={inputCls} value={form.suburb} onChange={(e) => set('suburb', e.target.value)} /></Field>
        <Field label="City" className="col-span-2 md:col-span-1"><input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="Postal code" className="col-span-2 md:col-span-1"><input className={inputCls} value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} maxLength={10} /></Field>

        <div className="col-span-6 mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">Banking details — printed on patient statements</div>
        <Field label="Bank" className="col-span-6 md:col-span-2"><input className={inputCls} value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} /></Field>
        <Field label="Account name" className="col-span-6 md:col-span-2"><input className={inputCls} value={form.bank_account_name} onChange={(e) => set('bank_account_name', e.target.value)} /></Field>
        <Field label="Account number" className="col-span-3 md:col-span-1"><input className={`${inputCls} font-mono`} value={form.bank_account_number} onChange={(e) => set('bank_account_number', e.target.value)} maxLength={20} /></Field>
        <Field label="Branch code" className="col-span-3 md:col-span-1"><input className={`${inputCls} font-mono`} value={form.bank_branch_code} onChange={(e) => set('bank_branch_code', e.target.value)} maxLength={10} /></Field>
        <Field label="Message on statements (optional)" className="col-span-6"><input className={inputCls} value={form.statement_message} onChange={(e) => set('statement_message', e.target.value)} placeholder="e.g. Accounts are payable within 30 days. Thank you." maxLength={500} /></Field>

        <label className="col-span-6 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
          <input type="checkbox" className="mt-1 rounded border-slate-300" checked={!!form.doctors_may_submit} onChange={(e) => set('doctors_may_submit', e.target.checked)} />
          <span>
            <span className="block text-sm font-semibold text-slate-700">Doctors may submit their own claims</span>
            <span className="block text-xs text-slate-500">Leave off when a billing team or bureau reviews every invoice (the usual set-up): doctors capture cases and send them to billing. Turn on for a solo doctor without a billing team.</span>
          </span>
        </label>

        <div className="col-span-6 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm">
            {errors.map((er) => <p key={er} className="text-red-600">{er}</p>)}
            {saved && <p className="flex items-center gap-1 text-green-700"><CheckCircleIcon className="h-4 w-4" />Saved</p>}
            {!saved && !errors.length && meta?.updated_by_name && (
              <p className="text-xs text-slate-400">Last changed {new Date(meta.updated_at).toLocaleString('en-ZA')} by {meta.updated_by_name}</p>
            )}
          </div>
          <button type="submit" disabled={saving} className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save practice details'}
          </button>
        </div>
      </form>

      <div className="border-t border-slate-100 px-5 py-4">
        <h3 className="text-sm font-bold text-slate-700">Hospital practice numbers</h3>
        <p className="mb-3 text-xs text-slate-500">The place-of-service number for each hospital on a claim. Hospitals are added from the Theatre Schedule.</p>
        {hospitals.length === 0 ? (
          <p className="text-sm text-slate-400">No hospitals yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {hospitals.map((h) => {
              const value = hospitalEdits[h.hid] ?? h.hpractice_number ?? '';
              const dirty = hospitalEdits[h.hid] !== undefined && hospitalEdits[h.hid] !== (h.hpractice_number || '');
              return (
                <li key={h.hid} className={`flex flex-wrap items-center gap-3 py-2 ${h.his_active ? '' : 'opacity-50'}`}>
                  <span className="min-w-[12rem] flex-1 text-sm text-slate-700">{h.hname}{h.hshort ? <span className="text-slate-400"> · {h.hshort}</span> : null}</span>
                  <input
                    className={`${inputCls} w-36 font-mono`}
                    value={value}
                    placeholder="7 digits"
                    maxLength={9}
                    onChange={(e) => { setHospitalEdits((ed) => ({ ...ed, [h.hid]: e.target.value })); setHospitalMsg((m) => ({ ...m, [h.hid]: '' })); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveHospital(h); } }}
                  />
                  <button type="button" onClick={() => saveHospital(h)} disabled={!dirty} className="rounded-lg border border-brand-navy px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-slate-50 disabled:opacity-30">
                    Save
                  </button>
                  <span className={`w-40 text-xs ${hospitalMsg[h.hid] === 'Saved' ? 'text-green-700' : hospitalMsg[h.hid] === 'Saving…' ? 'text-slate-400' : 'text-red-600'}`}>{hospitalMsg[h.hid]}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};

const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none transition-shadow focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';

const Field = ({ label, hint, className = '', children }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[0.8rem] font-semibold text-slate-600">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
  </label>
);

export default PracticeDetails;
